/**
 * List query, vote toggling and report creation for the Kehitys board.
 */

import { gql, useMutation, useQuery } from "@apollo/client";

import type { FeedbackKind, FeedbackReport, FeedbackReportContext } from "@/types";
import type { FeedbackSort } from "@/lib/feedbackMeta";

const ATTACHMENT_FIELDS = gql`
  fragment FeedbackAttachmentFields on FeedbackAttachmentType {
    id
    mediaType
    filename
    caption
    contentType
    sizeBytes
    width
    height
    durationSeconds
    url
    thumbUrl
    createdAt
  }
`;

export const REPORT_ROW_FIELDS = gql`
  fragment FeedbackReportRowFields on FeedbackReportType {
    id
    reference
    kind
    status
    title
    description
    voteCount
    commentCount
    createdById
    projectId
    viewerHasVoted
    viewerCanEdit
    viewerCanDelete
    version
    createdAt
    updatedAt
    createdBy {
      id
      firstName
      lastName
    }
    attachments {
      ...FeedbackAttachmentFields
    }
  }
  ${ATTACHMENT_FIELDS}
`;

export const GET_FEEDBACK_REPORTS = gql`
  query GetFeedbackReports(
    $kind: String
    $status: String
    $openOnly: Boolean
    $search: String
    $sort: String
    $limit: Int
    $offset: Int
  ) {
    feedbackReports(
      kind: $kind
      status: $status
      openOnly: $openOnly
      search: $search
      sort: $sort
      limit: $limit
      offset: $offset
    ) {
      items {
        ...FeedbackReportRowFields
      }
      totalCount
      hasNextPage
    }
  }
  ${REPORT_ROW_FIELDS}
`;

const TOGGLE_FEEDBACK_VOTE = gql`
  mutation ToggleFeedbackVote($reportId: String!) {
    toggleFeedbackVote(reportId: $reportId) {
      success
      message
      voted
      voteCount
    }
  }
`;

const DELETE_FEEDBACK_REPORT = gql`
  mutation DeleteFeedbackReport($id: String!) {
    deleteFeedbackReport(id: $id) {
      success
      message
    }
  }
`;

const CREATE_FEEDBACK_REPORT = gql`
  mutation CreateFeedbackReport($input: FeedbackReportCreateInput!) {
    createFeedbackReport(input: $input) {
      success
      message
      report {
        ...FeedbackReportRowFields
      }
    }
  }
  ${REPORT_ROW_FIELDS}
`;

export interface AttachmentRefInput {
  id: string;
  caption?: string | null;
}

export interface CreateReportInput {
  kind: FeedbackKind;
  title: string;
  description?: string | null;
  projectId?: string | null;
  context?: FeedbackReportContext | null;
  attachments: AttachmentRefInput[];
}

export interface FeedbackFilters {
  kind?: FeedbackKind | null;
  /** "" means open reports only; "ALL" means every status. */
  status?: string;
  search?: string;
  sort?: FeedbackSort;
}

interface ReportsQueryResult {
  feedbackReports: {
    items: FeedbackReport[];
    totalCount: number;
    hasNextPage: boolean;
  };
}

/** Translate the UI's filter shape into query variables. */
function toVariables(filters: FeedbackFilters) {
  const status = filters.status && filters.status !== "ALL" ? filters.status : null;
  return {
    kind: filters.kind ?? null,
    status,
    // openOnly only applies when no explicit status is chosen. "ALL" turns it
    // off so closed reports become visible.
    openOnly: !status && filters.status !== "ALL",
    search: filters.search?.trim() || null,
    sort: filters.sort ?? "votes",
    limit: 50,
    offset: 0,
  };
}

export function useFeedbackReports(filters: FeedbackFilters) {
  const variables = toVariables(filters);

  const { data, loading, error, refetch } = useQuery<ReportsQueryResult>(
    GET_FEEDBACK_REPORTS,
    { variables, fetchPolicy: "cache-and-network" },
  );

  const [voteMutation] = useMutation(TOGGLE_FEEDBACK_VOTE);
  const [deleteMutation] = useMutation(DELETE_FEEDBACK_REPORT);
  const [createMutation] = useMutation(CREATE_FEEDBACK_REPORT, {
    refetchQueries: [{ query: GET_FEEDBACK_REPORTS, variables }],
    awaitRefetchQueries: true,
  });

  /**
   * Toggle a vote and patch the row in place.
   *
   * The server is the authority on direction and count, so we write exactly what
   * it returns rather than incrementing locally. No refetch: re-sorting the list
   * under the user's cursor the instant they vote is disorienting.
   */
  const toggleVote = async (reportId: string) => {
    const result = await voteMutation({
      variables: { reportId },
      update(cache, { data: mutationData }) {
        const payload = mutationData?.toggleFeedbackVote;
        if (!payload?.success) return;
        cache.modify({
          id: cache.identify({ __typename: "FeedbackReportType", id: reportId }),
          fields: {
            voteCount: () => payload.voteCount,
            viewerHasVoted: () => payload.voted,
          },
        });
      },
    });
    return result.data?.toggleFeedbackVote;
  };

  const createReport = async (input: CreateReportInput) => {
    const result = await createMutation({ variables: { input } });
    return result.data?.createFeedbackReport;
  };

  /**
   * Soft-delete a report. Triage only (MANAGE_FEEDBACK, INTERNAL+).
   *
   * Lives on the list hook rather than the detail hook because the control is a
   * row action and the visible outcome is the list losing a row.
   */
  const deleteReport = async (reportId: string) => {
    const result = await deleteMutation({ variables: { id: reportId } });
    if (result.data?.deleteFeedbackReport?.success) await refetch();
    return result.data?.deleteFeedbackReport;
  };

  return {
    reports: data?.feedbackReports.items ?? [],
    totalCount: data?.feedbackReports.totalCount ?? 0,
    hasNextPage: data?.feedbackReports.hasNextPage ?? false,
    loading,
    error,
    refetch,
    toggleVote,
    createReport,
    deleteReport,
  };
}
